import { doc, updateDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { iosListCalendarEvents, iosCreateCalendarEvent } from '@/lib/iosCalendar'
import type { IosCalendarOverride } from '@/types/booking'
import { addDays, startOfDay, endOfDay } from 'date-fns'

/**
 * Sync non-booking iOS calendar events to Firestore availability overrides
 * Fetches events from the device calendar and stores as iosOverrides
 * Note: Booking events (created by the app) are tracked separately via booking.iosCalendarEventId
 */
export const iosSyncCalendarToAvailability = async (
  artistId: string,
  daysAhead: number = 90,
  existingBookingEventIds: string[] = []
): Promise<void> => {
  try {
    console.log('[IosAvailabilitySync] Starting calendar sync for', daysAhead, 'days')

    const now = new Date()
    const futureDate = addDays(now, daysAhead)

    // Fetch events from iOS calendar
    const events = await iosListCalendarEvents(now, futureDate)

    if (!events || events.length === 0) {
      console.log('[IosAvailabilitySync] No calendar events found')
      return
    }

    // Filter out booking events (which we already track via booking.iosCalendarEventId)
    // Only include non-booking calendar events
    const nonBookingEvents = events.filter(
      event => !existingBookingEventIds.includes(event.id)
    )

    // Convert iOS calendar events to iosOverrides format
    const iosOverrides: IosCalendarOverride[] = nonBookingEvents.map(event => ({
      eventId: event.id,
      eventTitle: event.title || 'Calendar Event',
      startDate: event.startDate,
      endDate: event.endDate,
      isBlocked: true // Calendar events block availability
    }))

    console.log('[IosAvailabilitySync] Found', iosOverrides.length, 'non-booking calendar events')

    // Update Firestore with new overrides
    const bookingSystemRef = doc(db, 'bookingSystems', artistId)
    await updateDoc(bookingSystemRef, {
      'availability.iosOverrides': iosOverrides
    })

    console.log('[IosAvailabilitySync] Availability synced successfully')
  } catch (error) {
    console.error('[IosAvailabilitySync] Error syncing calendar:', error)
    throw error
  }
}

/**
 * Create a booking event in iOS calendar
 * Called when a booking is confirmed
 * Note: Event is tracked via booking.iosCalendarEventId, NOT added to iosOverrides
 */
export const iosCreateBookingCalendarEvent = async (
  booking: {
    clientUsername: string
    shortSummary: string
    scheduledTimestamp: Date
    estimatedTime?: number  // in minutes
  }
): Promise<string | null> => {
  try {
    console.log('[IosAvailabilitySync] Creating booking event in iOS calendar')

    const startTime = booking.scheduledTimestamp.getTime()
    const endTime = new Date(
      startTime + (booking.estimatedTime || 60) * 60 * 1000
    ).getTime()

    // Create event in iOS calendar
    const eventId = await iosCreateCalendarEvent({
      title: `Client: ${booking.clientUsername}`,
      startDate: startTime,
      endDate: endTime,
      description: booking.shortSummary,
    })

    if (!eventId) {
      console.warn('[IosAvailabilitySync] Failed to create calendar event')
      return null
    }

    console.log('[IosAvailabilitySync] Booking event created:', eventId)
    // Event is stored in booking.iosCalendarEventId, not in availability.iosOverrides
    return eventId
  } catch (error) {
    console.error('[IosAvailabilitySync] Error creating booking event:', error)
    throw error
  }
}

/**
 * Delete a booking event from iOS calendar
 * Called when a booking is cancelled
 */
export const iosDeleteBookingCalendarEvent = async (
  eventId: string
): Promise<void> => {
  try {
    console.log('[IosAvailabilitySync] Deleting booking event:', eventId)

    // Delete from iOS calendar
    // May fail if user has already deleted it - that's ok
    try {
      const { iosDeleteCalendarEvent } = await import('@/lib/iosCalendar')
      await iosDeleteCalendarEvent(eventId)
      console.log('[IosAvailabilitySync] Deleted from iOS calendar')
    } catch (calendarError) {
      console.warn('[IosAvailabilitySync] Could not delete from calendar:', calendarError)
      // Continue anyway - event may have been manually deleted
    }
  } catch (error) {
    console.error('[IosAvailabilitySync] Error deleting booking event:', error)
    throw error
  }
}

/**
 * Check if a time slot overlaps with any iOS calendar events
 */
export const iosIsTimeSlotBlocked = (
  startTime: Date,
  endTime: Date,
  iosOverrides: IosCalendarOverride[] = []
): boolean => {
  const requestStart = startTime.getTime()
  const requestEnd = endTime.getTime()

  return iosOverrides.some(override => {
    // Check if there's any overlap
    return (
      (override.startDate < requestEnd && override.endDate > requestStart) &&
      override.isBlocked
    )
  })
}

/**
 * Get all blocked time slots for a specific date from iOS calendar events
 */
export const iosGetBlockedTimeSlotsForDate = (
  date: Date,
  iosOverrides: IosCalendarOverride[] = []
): Array<{ startDate: number; endDate: number }> => {
  const dayStart = startOfDay(date).getTime()
  const dayEnd = endOfDay(date).getTime()

  return iosOverrides
    .filter(override => {
      // Check if event is on this date and is blocked
      return (
        override.isBlocked &&
        override.startDate < dayEnd &&
        override.endDate > dayStart
      )
    })
    .map(override => ({
      startDate: Math.max(override.startDate, dayStart),
      endDate: Math.min(override.endDate, dayEnd)
    }))
}

